import { Box, Center, Spinner, Text } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SourcingEventStatus, ViewableEntityKind, useGetSourcingEventQuery } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { useRecordRecentlyViewed } from "../../common/recently-viewed/useRecordRecentlyViewed";
import { urls } from "../../urls";
import { ChangeResponsiblePersonButton } from "./ChangeResponsiblePersonButton";
import { useCanManageSourcingEvent } from "./useCanManageSourcingEvent";
import { DraftNoEditAccessBanner } from "./DraftNoEditAccessBanner";
import { EventHeader } from "./EventHeader";
import { EventLoadFailed, EventUnavailable } from "./EventUnavailable";
import { isNoAccessError } from "../../common/errors/isNoAccessError";
import { PublicationStatusBanner } from "./PublicationStatusBanner";
import { ShareEventButton } from "./ShareEventButton";
import { EventPhaseTimeline } from "./timeline/EventPhaseTimeline";
import { EventNowPanel } from "./now/EventNowPanel";
import { EVENT_TABS_ANCHOR_ID } from "./eventTabsAnchor";
import { useGoToEventTab } from "./useGoToEventTab";
import { useRevealAnchor } from "./useRevealAnchor";
import { EventTabs, parseEventTab, type EventTab } from "./EventTabs";
import { defaultEventTab } from "./defaultEventTab";
import { visibleEventTabs } from "./eventTabDefinitions";
import { tabToNormalizeTo } from "./tabUrlNormalization";
import { RoundSelectionProvider } from "./rounds/RoundSelectionContext";
import { AwardSelectionProvider } from "./award/AwardSelectionContext";

const PENDING_POLL_MS = 15000;

export const SourcingEventView = () => {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const goToTab = useGoToEventTab();
  useRevealAnchor();
  const { selectedOrg } = useLoggedInWithOrgContextState();

  const [pollInterval, setPollInterval] = useState(0);
  const [opened, setOpened] = useState(() => ({ eventId, at: Date.now() }));
  // React Router reuses this component across a :eventId change, so a mount-only clock stays event A's.
  if (opened.eventId !== eventId) setOpened({ eventId, at: Date.now() });
  const openedAt = opened.at;
  const { data: event, isLoading, isFetching, error, refetch } = useGetSourcingEventQuery(
    eventId ? { eventId } : skipToken,
    { pollingInterval: pollInterval },
  );
  const canManage = useCanManageSourcingEvent(event);
  useEffect(() => {
    setPollInterval(event?.status === SourcingEventStatus.PublicationPending ? PENDING_POLL_MS : 0);
  }, [event?.status]);
  useRecordRecentlyViewed(ViewableEntityKind.SourcingEvent, eventId);

  if (isLoading) {
    return (
      <Center py="20">
        <Spinner color="smPrimary" />
      </Center>
    );
  }

  // Gate on missing data, not isError — a transient poll failure must not blank the page.
  if (!event) {
    if (error && eventId) {
      return isNoAccessError(error) ? (
        <EventUnavailable eventId={eventId} />
      ) : (
        <EventLoadFailed onRetry={() => void refetch()} isRetrying={isFetching} />
      );
    }
    return (
      <Center py="20">
        <Text color="smError">{t("Could not load this sourcing event.")}</Text>
      </Center>
    );
  }

  const isDraft = event.status === SourcingEventStatus.Draft;
  if (isDraft && canManage) {
    return <Navigate to={urls.app.sourcingEvents.edit.go(event.id).info} replace />;
  }

  const requestedTab = searchParams.get("tab");
  const landingTab = defaultEventTab(event, openedAt);
  const normalizeTo = tabToNormalizeTo(
    requestedTab,
    parseEventTab(requestedTab),
    visibleEventTabs(event).map((tab) => tab.key),
    landingTab,
  );
  const activeTab = normalizeTo ?? parseEventTab(requestedTab) ?? landingTab;

  const onViewPublic =
    event.eventContext.type === "Public" ? () => navigate(urls.app.supplier.procurement.go(event.id)) : undefined;

  return (
    <RoundSelectionProvider event={event}>
      <AwardSelectionProvider event={event}>
        <Box>
          <EventHeader
            event={event}
            onViewPublic={onViewPublic}
            responsibleEditControl={
              event.responsiblePerson && (
                // The buyer view is always scoped to the owning org (the BFF rejects access from other
                // orgs), so the current org is the event owner — the roster to pick a new responsible from.
                <ChangeResponsiblePersonButton
                  canManage={canManage}
                  eventId={event.id}
                  ownerOrgId={selectedOrg.id}
                  homeDepartmentId={event.homeDepartment?.id}
                  currentResponsible={event.responsiblePerson}
                />
              )
            }
            shareControl={<ShareEventButton event={event} ownerOrgId={selectedOrg.id} />}
          />
          {isDraft && <DraftNoEditAccessBanner />}
          <PublicationStatusBanner
            status={event.status}
            publications={event.eventContext.type === "Public" ? event.eventContext.publications : undefined}
          />
          <EventPhaseTimeline event={event} />
          <EventNowPanel event={event} activeTab={activeTab} />
          <Box id={EVENT_TABS_ANCHOR_ID} mt="6">
            <EventTabs
              event={event}
              activeTab={activeTab}
              normalizeTo={normalizeTo}
              onTabChange={goToTab}
            />
          </Box>
        </Box>
      </AwardSelectionProvider>
    </RoundSelectionProvider>
  );
};
